import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../../database/firebase";

// Register user thunk
export const registerUser = createAsyncThunk(
  "auth/register",
  async (
    { email, password, displayName, role = "user" },
    { rejectWithValue }
  ) => {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Create user document in Firestore 'users' collection
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        role: role,
        photoURL: null,
        onboarding_completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "users", user.uid), userData);

      return {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        role: role,
        onboarding_completed: false,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Login user thunk
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Fetch user data from Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          uid: user.uid,
          email: user.email,
          displayName:
            userData.personalDetails?.name ||
            userData.displayName ||
            user.displayName,
          role: userData.profile?.role || userData.role || "user",
          photoURL: userData.profile?.photoURL || userData.photoURL || null,
          personalDetails: userData.personalDetails || {},
          profile: userData.profile || {},
          onboardingResponses: userData.onboardingResponses || {},
          // Keep the complete user data for access to all fields
          ...userData,
        };
      } else {
        // If no user document exists, create one
        const newUserData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || "User",
          role: "user",
          photoURL: user.photoURL || null,
          onboarding_completed: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await setDoc(doc(db, "users", user.uid), newUserData);
        return newUserData;
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Logout user thunk
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check auth state thunk
export const checkAuthState = createAsyncThunk(
  "auth/checkAuthState",
  async (_, { rejectWithValue }) => {
    try {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            if (user) {
              // Fetch user data from Firestore
              const userDoc = await getDoc(doc(db, "users", user.uid));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                resolve({
                  uid: user.uid,
                  email: user.email,
                  displayName:
                    userData.personalDetails?.name ||
                    userData.displayName ||
                    user.displayName,
                  role: userData.profile?.role || userData.role || "user",
                  photoURL:
                    userData.profile?.photoURL || userData.photoURL || null,
                  personalDetails: userData.personalDetails || {},
                  profile: userData.profile || {},
                  onboardingResponses: userData.onboardingResponses || {},
                  // Keep the complete user data for access to all fields
                  ...userData,
                });
              } else {
                resolve(null);
              }
            } else {
              resolve(null);
            }
          },
          reject
        );
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Check Auth State
    builder
      .addCase(checkAuthState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(checkAuthState.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
